import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    FaBold, FaItalic, FaUnderline, FaStrikethrough, FaRemoveFormat,
    FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
    FaListUl, FaListOl, FaOutdent, FaIndent, FaSearch, FaTimes, FaEyeDropper,
} from 'react-icons/fa';
import '../css/financeiro-parcela-pagar.css';

const MARGEM = 12;

// Rotulos em portugues: o config do CKEditor guarda as opcoes pelo nome interno, em ingles.
const NOME_TAMANHO = { tiny: 'Minúsculo', small: 'Pequeno', big: 'Grande', huge: 'Enorme' };
const NOME_ESTILO = {
    paragraph: 'Parágrafo', heading1: 'Título 1', heading2: 'Título 2',
    heading3: 'Título 3', heading4: 'Título 4', heading5: 'Título 5', heading6: 'Título 6',
};
const NOME_COR = {
    'Black': 'Preto', 'Dim grey': 'Cinza escuro', 'Grey': 'Cinza', 'Light grey': 'Cinza claro',
    'White': 'Branco', 'Red': 'Vermelho', 'Orange': 'Laranja', 'Yellow': 'Amarelo',
    'Light green': 'Verde claro', 'Green': 'Verde', 'Aquamarine': 'Água-marinha',
    'Turquoise': 'Turquesa', 'Light blue': 'Azul claro', 'Blue': 'Azul', 'Purple': 'Roxo',
};

const MARCAS = [
    { comando: 'bold', Icone: FaBold, titulo: 'Negrito (Ctrl+B)' },
    { comando: 'italic', Icone: FaItalic, titulo: 'Itálico (Ctrl+I)' },
    { comando: 'underline', Icone: FaUnderline, titulo: 'Sublinhado (Ctrl+U)' },
    { comando: 'strikethrough', Icone: FaStrikethrough, titulo: 'Tachado' },
];

const ALINHAMENTOS = [
    { valor: 'left', Icone: FaAlignLeft, titulo: 'Alinhar à esquerda' },
    { valor: 'center', Icone: FaAlignCenter, titulo: 'Centralizar' },
    { valor: 'right', Icone: FaAlignRight, titulo: 'Alinhar à direita' },
    { valor: 'justify', Icone: FaAlignJustify, titulo: 'Justificar' },
];

const PARAGRAFO = [
    { comando: 'bulletedList', Icone: FaListUl, titulo: 'Lista com marcadores' },
    { comando: 'numberedList', Icone: FaListOl, titulo: 'Lista numerada' },
    { comando: 'outdent', Icone: FaOutdent, titulo: 'Diminuir recuo' },
    { comando: 'indent', Icone: FaIndent, titulo: 'Aumentar recuo' },
];

/*
 * As opcoes saem do config do proprio editor, no mesmo formato de valor que os plugins de fonte
 * do CKEditor geram a partir dele. Valor em outro formato o comando aceita, mas o editor nao sabe
 * desenhar: o texto fica igual e nada acusa.
 */
function opcoesDeTamanho(editor) {
    return (editor.config.get('fontSize.options') || []).map((op) => {
        if (op === 'default') return { titulo: 'Normal', valor: undefined };
        if (typeof op === 'object') return { titulo: op.title, valor: op.model };
        if (NOME_TAMANHO[op]) return { titulo: NOME_TAMANHO[op], valor: op };
        return { titulo: String(op), valor: `${parseFloat(op)}px` };
    });
}

function opcoesDeFonte(editor) {
    return (editor.config.get('fontFamily.options') || []).map((op) => {
        if (op === 'default') return { titulo: 'Padrão', valor: undefined };
        if (typeof op === 'object') return { titulo: op.title, valor: op.model };
        const nomes = op.replace(/"|'/g, '').split(',').map((nome) => nome.trim());
        return {
            titulo: nomes[0],
            valor: nomes.map((nome) => (nome.indexOf(' ') > 0 ? `"${nome}"` : nome)).join(', '),
        };
    });
}

// O seletor nativo so aceita #rrggbb. Cor atual em outro formato (a paleta do CKEditor usa hsl)
// abre o seletor no preto.
function hexParaSeletor(cor) {
    return /^#[0-9a-f]{6}$/i.test(cor || '') ? cor : '#000000';
}

// As cores ja usadas no texto, como a secao "Cores do documento" do dropdown do CKEditor: o atributo
// do comando lido direto do modelo, na ordem em que aparecem. Respeita o `documentColors` da
// configuracao (padrao 10; 0 desliga, como na barra).
function coresDoDocumento(editor, comando) {
    const limite = editor.config.get(`${comando}.documentColors`) ?? 10;
    const cores = [];
    if (!limite) return cores;
    for (const raiz of editor.model.document.getRoots()) {
        for (const item of editor.model.createRangeIn(raiz).getItems()) {
            const cor = item.is('$textProxy') ? item.getAttribute(comando) : null;
            if (cor && !cores.includes(cor)) {
                cores.push(cor);
                if (cores.length >= limite) return cores;
            }
        }
    }
    return cores;
}

function coresDoConfig(editor, comando) {
    return (editor.config.get(`${comando}.colors`) || []).map((c) => (typeof c === 'string'
        ? { cor: c, rotulo: c }
        : { cor: c.color, rotulo: NOME_COR[c.label] || c.label || c.color }));
}

function filtrarTags(tags, busca) {
    const termo = busca.trim().toLowerCase();
    if (!termo) return tags;
    return tags
        .map((categoria) => ({
            ...categoria,
            tags: categoria.tags.filter((t) => t.label.toLowerCase().includes(termo) || t.tag.toLowerCase().includes(termo)),
        }))
        .filter((categoria) => categoria.tags.length > 0);
}

// Aba da ultima abertura, para quem so usa tags nao precisar trocar de aba a cada clique.
let abaLembrada = 'formatacao';

/**
 * Caixa do clique direito no CKEditor: atalho para a formatacao e para as tags, para quem edita
 * uma carta longa nao precisar voltar a barra do topo. Nao substitui a barra: chama os mesmos
 * comandos nativos do editor (`editor.execute`), entao desfazer e o HTML gerado sao os mesmos.
 *
 * Os botoes nao tomam o foco (preventDefault no mousedown): a selecao continua visivel e o comando
 * age sobre ela. A caixa segue aberta depois de formatar, para aplicar mais de uma marca; fecha no
 * Esc, clicando fora, rolando a pagina ou ao inserir uma tag.
 *
 * `tags` e opcional: sem elas a caixa mostra so a formatacao.
 */
export default function MenuContextoEditor({ editor, posicao, tags, onInserirTag, onFechar }) {
    const caixaRef = useRef(null);
    const buscaRef = useRef(null);
    const fecharRef = useRef(onFechar);
    fecharRef.current = onFechar;

    const [aba, setAba] = useState(tags ? abaLembrada : 'formatacao');
    const [busca, setBusca] = useState('');
    const [lugar, setLugar] = useState(null);
    // Os valores dos comandos mudam ao aplicar uma formatacao; isto so forca a releitura deles.
    const [, setVersao] = useState(0);

    // Posiciona depois de medir, para a caixa nao sair da tela perto da borda.
    useLayoutEffect(() => {
        const caixa = caixaRef.current;
        if (!caixa) return;
        setLugar({
            left: Math.max(MARGEM, Math.min(posicao.x, window.innerWidth - caixa.offsetWidth - MARGEM)),
            top: Math.max(MARGEM, Math.min(posicao.y, window.innerHeight - caixa.offsetHeight - MARGEM)),
        });
    }, [posicao, aba]);

    // A busca de tags so recebe foco com a caixa ja posicionada. Com `autoFocus` o foco era pedido
    // antes do posicionamento, com a caixa ainda em `visibility: hidden`, e elemento invisivel nao
    // recebe foco: o menu que abria direto na aba Tags deixava o foco no editor, e o filtro digitado
    // entrava na carta.
    useEffect(() => {
        if (lugar && aba === 'tags' && buscaRef.current) {
            buscaRef.current.focus({ preventScroll: true });
        }
    }, [lugar, aba]);

    useEffect(() => {
        const dentro = (alvo) => caixaRef.current && caixaRef.current.contains(alvo);
        const aoClicar = (e) => { if (!dentro(e.target)) fecharRef.current(); };
        const aoRolar = (e) => { if (!dentro(e.target)) fecharRef.current(); };
        const aoRedimensionar = () => fecharRef.current();
        const aoTeclar = (e) => {
            if (e.key !== 'Escape') return;
            fecharRef.current();
            editor.editing.view.focus();
        };
        document.addEventListener('mousedown', aoClicar);
        document.addEventListener('keydown', aoTeclar);
        window.addEventListener('scroll', aoRolar, true);
        window.addEventListener('resize', aoRedimensionar);
        return () => {
            document.removeEventListener('mousedown', aoClicar);
            document.removeEventListener('keydown', aoTeclar);
            window.removeEventListener('scroll', aoRolar, true);
            window.removeEventListener('resize', aoRedimensionar);
        };
    }, [editor]);

    const comando = (nome) => editor.commands.get(nome);

    const executar = (nome, opcoes) => {
        if (!comando(nome)) return;
        editor.execute(nome, opcoes);
        editor.editing.view.focus();
        setVersao((v) => v + 1);
    };

    const trocarAba = (nova) => {
        abaLembrada = nova;
        setAba(nova);
    };

    // Sem isto o clique tira o foco do editor e a selecao some da tela.
    const semFoco = (e) => e.preventDefault();

    // `ativo` so nos botoes que alternam (marca, alinhamento, lista): nos outros fica undefined e o
    // aria-pressed nem e escrito, para o leitor de tela nao anunciar como liga-desliga.
    const botaoIcone = ({ chave, Icone, titulo, ativo, habilitado = true, onClick }) => (
        <button
            key={chave}
            type="button"
            className={`fin-pp__ctx-btn${ativo ? ' is-active' : ''}`}
            title={titulo}
            aria-label={titulo}
            aria-pressed={ativo}
            disabled={!habilitado}
            onMouseDown={semFoco}
            onClick={onClick}>
            <Icone size={13} />
        </button>
    );

    const renderFormatacao = () => {
        const estiloAtual = comando('heading')?.value || 'paragraph';
        const tamanhoAtual = comando('fontSize')?.value;
        const fonteAtual = comando('fontFamily')?.value;
        const alinhamentoAtual = comando('alignment')?.value;

        return (
            <div className="fin-pp__ctx-body">
                <div className="fin-pp__ctx-row">
                    {MARCAS.filter((m) => comando(m.comando)).map((m) => botaoIcone({
                        chave: m.comando,
                        Icone: m.Icone,
                        titulo: m.titulo,
                        ativo: comando(m.comando).value === true,
                        habilitado: comando(m.comando).isEnabled,
                        onClick: () => executar(m.comando),
                    }))}
                    {comando('removeFormat') ? (
                        <>
                            <span className="fin-pp__ctx-sep" />
                            {botaoIcone({
                                chave: 'removeFormat',
                                Icone: FaRemoveFormat,
                                titulo: 'Remover formatação',
                                habilitado: comando('removeFormat').isEnabled,
                                onClick: () => executar('removeFormat'),
                            })}
                        </>
                    ) : null}
                </div>

                <div className="fin-pp__ctx-row">
                    {comando('alignment') ? ALINHAMENTOS.map((a) => botaoIcone({
                        chave: a.valor,
                        Icone: a.Icone,
                        titulo: a.titulo,
                        ativo: alinhamentoAtual === a.valor,
                        habilitado: comando('alignment').isEnabled,
                        onClick: () => executar('alignment', { value: a.valor }),
                    })) : null}
                    <span className="fin-pp__ctx-sep" />
                    {PARAGRAFO.filter((p) => comando(p.comando)).map((p) => botaoIcone({
                        chave: p.comando,
                        Icone: p.Icone,
                        titulo: p.titulo,
                        ativo: typeof comando(p.comando).value === 'boolean' ? comando(p.comando).value : undefined,
                        habilitado: comando(p.comando).isEnabled,
                        onClick: () => executar(p.comando),
                    }))}
                </div>

                {comando('heading') ? (
                    <div>
                        <div className="fin-pp__ctx-label">Estilo</div>
                        <div className="fin-pp__ctx-row">
                            {(editor.config.get('heading.options') || []).map((op) => (
                                <button
                                    key={op.model}
                                    type="button"
                                    className={`fin-pp__ctx-btn${estiloAtual === op.model ? ' is-active' : ''}`}
                                    disabled={!comando('heading').isEnabled}
                                    onMouseDown={semFoco}
                                    onClick={() => (op.model === 'paragraph'
                                        ? executar('paragraph')
                                        : executar('heading', { value: op.model }))}>
                                    {NOME_ESTILO[op.model] || op.title}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {comando('fontSize') ? (
                    <div>
                        <div className="fin-pp__ctx-label">Tamanho</div>
                        <div className="fin-pp__ctx-row">
                            {opcoesDeTamanho(editor).map((op) => (
                                <button
                                    key={op.titulo}
                                    type="button"
                                    className={`fin-pp__ctx-btn${tamanhoAtual === op.valor ? ' is-active' : ''}`}
                                    disabled={!comando('fontSize').isEnabled}
                                    onMouseDown={semFoco}
                                    onClick={() => executar('fontSize', { value: op.valor })}>
                                    {op.titulo}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {comando('fontFamily') ? (
                    <div>
                        <div className="fin-pp__ctx-label">Fonte</div>
                        <select
                            className="fin-pp__select"
                            value={fonteAtual || ''}
                            disabled={!comando('fontFamily').isEnabled}
                            onChange={(e) => executar('fontFamily', { value: e.target.value || undefined })}>
                            {opcoesDeFonte(editor).map((op) => (
                                <option key={op.titulo} value={op.valor || ''}>{op.titulo}</option>
                            ))}
                        </select>
                    </div>
                ) : null}

                {[
                    { nome: 'fontColor', rotulo: 'Cor do texto' },
                    { nome: 'fontBackgroundColor', rotulo: 'Cor de fundo' },
                ].filter((c) => comando(c.nome)).map(({ nome, rotulo }) => {
                    const atual = comando(nome).value;
                    const paleta = coresDoConfig(editor, nome);
                    // So as que nao estao na paleta: essas ja tem quadrado na linha de cima.
                    const personalizadas = coresDoDocumento(editor, nome)
                        .filter((cor) => !paleta.some((c) => c.cor === cor));
                    return (
                        <div key={nome}>
                            <div className="fin-pp__ctx-label">{rotulo}</div>
                            <div className="fin-pp__ctx-swatches">
                                <button
                                    type="button"
                                    className={`fin-pp__ctx-swatch${!atual ? ' is-active' : ''}`}
                                    title="Sem cor"
                                    aria-label="Sem cor"
                                    disabled={!comando(nome).isEnabled}
                                    onMouseDown={semFoco}
                                    onClick={() => executar(nome, { value: undefined })}>
                                    <FaTimes size={8} />
                                </button>
                                {paleta.map((c) => (
                                    <button
                                        key={c.cor}
                                        type="button"
                                        className={`fin-pp__ctx-swatch${atual === c.cor ? ' is-active' : ''}`}
                                        style={{ background: c.cor }}
                                        title={c.rotulo}
                                        aria-label={c.rotulo}
                                        disabled={!comando(nome).isEnabled}
                                        onMouseDown={semFoco}
                                        onClick={() => executar(nome, { value: c.cor })} />
                                ))}
                            </div>
                            {/* Outra cor: o seletor nativo do navegador, aplicado pelo mesmo comando do
                                CKEditor. So no `change`, ao confirmar a cor: o onChange do React dispara a
                                cada arrasto dentro do seletor e empilharia um desfazer por passo. */}
                            <div className="fin-pp__ctx-swatches fin-pp__ctx-cor-linha">
                                <span className="fin-pp__ctx-swatch fin-pp__ctx-cor">
                                    <FaEyeDropper size={9} />
                                    <input
                                        type="color"
                                        aria-label="Outra cor"
                                        disabled={!comando(nome).isEnabled}
                                        defaultValue={hexParaSeletor(atual)}
                                        ref={(el) => {
                                            if (!el || el.dataset.ligado) return;
                                            el.dataset.ligado = '1';
                                            el.addEventListener('change', (e) => executar(nome, { value: e.target.value }));
                                        }} />
                                </span>
                                {personalizadas.map((cor) => (
                                    <button
                                        key={cor}
                                        type="button"
                                        className={`fin-pp__ctx-swatch${atual === cor ? ' is-active' : ''}`}
                                        style={{ background: cor }}
                                        title={cor}
                                        aria-label={cor}
                                        disabled={!comando(nome).isEnabled}
                                        onMouseDown={semFoco}
                                        onClick={() => executar(nome, { value: cor })} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTags = () => {
        const encontradas = filtrarTags(tags, busca);
        return (
            <div className="fin-pp__ctx-body">
                <div className="fin-pp__ctx-busca">
                    <FaSearch size={12} className="fin-pp__ctx-busca-ic" />
                    <input
                        ref={buscaRef}
                        type="text"
                        className="fin-pp__input"
                        placeholder="Buscar tag"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)} />
                </div>
                <div className="fin-pp__ctx-tags">
                    {encontradas.length === 0 ? (
                        <div className="fin-pp__ctx-vazio">Nenhuma tag encontrada</div>
                    ) : encontradas.map((categoria) => (
                        <div key={categoria.categoria}>
                            <div className="fin-pp__ctx-cat">{categoria.categoria}</div>
                            {categoria.tags.map((item) => (
                                <button
                                    key={item.tag}
                                    type="button"
                                    className="fin-pp__ctx-tag"
                                    onMouseDown={semFoco}
                                    onClick={() => onInserirTag(item)}>
                                    <span>{item.label}</span>
                                    <code>{item.tag}</code>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={caixaRef}
            className="fin-pp__ctx"
            role="dialog"
            aria-label="Formatação e tags do editor"
            style={{
                left: lugar ? lugar.left : posicao.x,
                top: lugar ? lugar.top : posicao.y,
                visibility: lugar ? 'visible' : 'hidden',
            }}
            onContextMenu={(e) => e.preventDefault()}>
            {tags ? (
                <div className="fin-pp__tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={aba === 'formatacao'}
                        className={`fin-pp__tab${aba === 'formatacao' ? ' is-active' : ''}`}
                        onMouseDown={semFoco}
                        onClick={() => trocarAba('formatacao')}>
                        Formatação
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={aba === 'tags'}
                        className={`fin-pp__tab${aba === 'tags' ? ' is-active' : ''}`}
                        onMouseDown={semFoco}
                        onClick={() => trocarAba('tags')}>
                        Tags
                    </button>
                </div>
            ) : null}

            {aba === 'tags' && tags ? renderTags() : renderFormatacao()}

            <div className="fin-pp__ctx-hint">Shift + clique direito abre o menu do navegador</div>
        </div>
    );
}
